<template>
  <!-- Side Navigation (Desktop Only) -->
  <div class="hidden md:flex fixed left-0 top-0 h-full w-20 flex-col items-center py-6 z-20" style="background-color: #69CBD8;">
    <!-- Navigation Items -->
    <div class="flex flex-col space-y-8 mt-8">
      <!-- Home -->
      <router-link 
        to="/" 
        class="nav-item group"
        :class="{ 'active': currentRoute === 'home' }"
        title="Home"
      >
        <div class="nav-icon-container">
          <svg xmlns="http://www.w3.org/2000/svg" class="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        </div>
        <span class="nav-label">Home</span>
      </router-link>

      <!-- Explore -->
      <router-link 
        to="/explore" 
        class="nav-item group"
        :class="{ 'active': currentRoute === 'explore' }"
        title="Explore"
      >
        <div class="nav-icon-container">
          <svg xmlns="http://www.w3.org/2000/svg" class="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <span class="nav-label">Explore</span>
      </router-link>

      <!-- Map -->
      <router-link 
        to="/map" 
        class="nav-item group"
        :class="{ 'active': currentRoute === 'map' }"
        title="Map"
      >
        <div class="nav-icon-container">
          <svg xmlns="http://www.w3.org/2000/svg" class="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
        </div>
        <span class="nav-label">Map</span>
      </router-link>

      <!-- Profile -->
      <router-link 
        to="/profile" 
        class="nav-item group"
        :class="{ 'active': currentRoute === 'profile' }"
        title="Profile"
      >
        <div class="nav-icon-container">
          <svg xmlns="http://www.w3.org/2000/svg" class="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <span class="nav-label">Profile</span>
      </router-link>
    </div>

    <!-- Logout Button at Bottom -->
    <div class="mt-auto mb-8">
      <button 
        @click="handleLogout"
        class="nav-item group logout-btn"
        title="Logout"
      >
        <div class="nav-icon-container">
          <svg xmlns="http://www.w3.org/2000/svg" class="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        </div>
        <span class="nav-label">Logout</span>
      </button>
    </div>
  </div>
</template>

<script>
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

export default {
  name: 'SideNavbar',
  props: {
    currentRoute: {
      type: String,
      default: 'home'
    }
  },
  setup() {
    const store = useStore();
    const router = useRouter();

    const handleLogout = async () => {
      try {
        await store.dispatch('logout');
        router.push('/login');
      } catch (error) {
        console.error('Logout error:', error);
      }
    };

    return {
      handleLogout
    };
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@100;600&display=swap');

.nav-item {
  @apply flex flex-col items-center justify-center p-3 rounded-xl transition-all duration-200 relative;
  font-family: 'Poppins', sans-serif;
  color: rgba(255, 255, 255, 0.7);
}

.nav-item:hover {
  @apply bg-white bg-opacity-20;
  color: white;
}

.nav-item.active {
  @apply bg-white bg-opacity-30;
  color: white;
}

.nav-icon-container {
  @apply flex items-center justify-center w-8 h-8 mb-1;
}

.nav-icon {
  @apply w-6 h-6;
}

.nav-label {
  @apply text-xs font-medium;
  font-size: 10px;
}

.logout-btn {
  @apply text-red-200;
}

.logout-btn:hover {
  @apply text-red-100 bg-red-500 bg-opacity-20;
}

/* Tooltip styles */
.nav-item::after {
  content: attr(title);
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 12px;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  z-index: 1000;
}

.nav-item:hover::after {
  opacity: 1;
}
</style>
